import React from "react";
import {
  Alert,
  Badge,
  Button,
  Col,
  Input,
  InputGroup,
  InputGroupText,
  Nav,
  NavItem,
  NavLink,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import {
  CoincidenciaAutomatica,
  RegistroPendienteConciliacion,
  ResumenFila,
  abreviarIdentificador,
  construirNombreCajero,
  describirOrigenConciliacion,
  formatCurrencyGs,
  formatFechaHoraTexto,
} from "./MonchisAutoConciliacion.helpers";

type CoincidenciaDetalleRowProps = {
  fila: CoincidenciaAutomatica;
  estadoColor: string;
  estadoTexto: string;
  estadoDescripcion: string;
  extraColumn?: React.ReactNode;
  actualizandoEstado?: boolean;
};

const CoincidenciaDetalleRow: React.FC<CoincidenciaDetalleRowProps> = ({
  fila,
  estadoColor,
  estadoTexto,
  estadoDescripcion,
  extraColumn,
  actualizandoEstado = false,
}) => {
  const turnoInicio =
    fila.registroInicioTurnoTexto ??
    formatFechaHoraTexto(fila.registroFechaHoraAperturaTurno, {
      hour: "2-digit",
      minute: "2-digit",
    });
  const turnoFin =
    fila.registroFinTurnoTexto ??
    formatFechaHoraTexto(fila.registroFechaCierre, {
      hour: "2-digit",
      minute: "2-digit",
    });
  const movimientoFecha = formatFechaHoraTexto(fila.movimientoFechaHora);
  const fechaDiaTexto =
    fila.registroFechaTexto ??
    formatFechaHoraTexto(fila.registroFechaDia, { dateStyle: "short" }) ??
    fila.registroFechaDia;
  const auditoriaTexto = [
    fila.registroIdTurno ? `Turno #${fila.registroIdTurno}` : null,
    fila.registroIdControlCaja ? `Caja #${fila.registroIdControlCaja}` : null,
    construirNombreCajero(fila.registroCajeroNombre, fila.registroCajeroApellido) !== "Sin dato"
      ? `Cajero: ${construirNombreCajero(fila.registroCajeroNombre, fila.registroCajeroApellido)}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <tr>
      <td className="align-top">
        <div className="fw-semibold text-primary text-break">
          Registro #{fila.registroNumeroMovimiento || fila.registroId}
        </div>
        <div className="text-muted small text-break">ID registro: {fila.registroId}</div>
        {fila.registroNumeroMovimiento && (
          <div className="text-muted small text-break">
            Movimiento #{fila.registroNumeroMovimiento}
          </div>
        )}
        <div className="text-muted small">Fecha día: {fechaDiaTexto ?? "—"}</div>
        <div className="text-muted small">Sucursal: {fila.registroSucursalNombre || "Sin sucursal"}</div>
        <div className="text-muted small">
          Rango turno: {turnoInicio ?? "—"} - {turnoFin ?? "—"}
        </div>
        <div className="text-muted small">
          {formatFechaHoraTexto(fila.registroFechaHoraAperturaTurno) ?? "—"} →{" "}
          {formatFechaHoraTexto(fila.registroFechaCierre) ?? "—"}
        </div>
        <div className="text-muted small mt-1">{auditoriaTexto || "Sin datos de turno/caja"}</div>
        <div className="text-muted small">
          Fecha registro: {formatFechaHoraTexto(fila.registroFechaApertura ?? fila.registroFechaDia) ?? "—"}
        </div>
        <div className="mt-1 text-muted small">Forma: 04.Monchis Pago Online</div>
        <div className="fw-semibold text-primary mt-2">{formatCurrencyGs(fila.registroMonto)}</div>
      </td>
      <td>
        <div className="fw-semibold text-success text-break">
          Movimiento #{fila.movimientoNroOrden || fila.movimientoId}
        </div>
        <div className="text-muted small text-break">ID movimiento: {fila.movimientoId}</div>
        <div className="text-muted small">Fecha · hora: {movimientoFecha ?? "Sin hora"}</div>
        <div className="text-muted small">{fila.movimientoSucursal || "Sin sucursal"}</div>
        <div className="text-muted small">Transacción #{fila.movimientoNroTransaccion ?? "N/D"}</div>
        <div className="text-muted small">Orden #{fila.movimientoNroOrden ?? "N/D"}</div>
        <div className="text-muted small">Integración #{fila.movimientoIdIntegracion ?? "N/D"}</div>
        <div className="text-muted small">Forma: {fila.movimientoTipoPago || "Sin dato"}</div>
        <div className="text-muted small">Estado: {fila.movimientoEstado || "Sin estado"}</div>
        <div className="fw-semibold text-success mt-2">
          {formatCurrencyGs(fila.movimientoImporteConDelivery ?? fila.movimientoImporteOrden ?? 0)}
        </div>
      </td>
      <td className="align-top">
        {actualizandoEstado ? (
          <div className="text-muted small d-flex align-items-center gap-2">
            <Spinner size="sm" /> Actualizando estado…
          </div>
        ) : (
          <>
            <Badge color={estadoColor} className="mb-2">
              {estadoTexto}
            </Badge>
            <div className="text-muted small text-break">{estadoDescripcion}</div>
            {fila.conciliacionId ? (
              <div className="text-muted small mt-2">
                Conciliado por {fila.conciliacionUsuarioNombre ?? `Usuario #${fila.conciliacionUsuarioId ?? "?"}`}{" "}
                {formatFechaHoraTexto(fila.conciliacionFecha) ?? ""} · Estado{" "}
                {fila.conciliacionEstado ?? "conciliado"}
              </div>
            ) : null}
            <div className="text-muted small mt-1">
              Tipo de conciliación:{" "}
              {fila.conciliacionOrigen === "automatica"
                ? "Automática (BaconApp)"
                : fila.conciliacionOrigen === "sugerida"
                  ? "Sugerida (para revisión)"
                  : fila.conciliacionOrigen === "manual"
                    ? "Manual"
                    : "Pendiente"}
            </div>
            {fila.conciliacionId ? (
              <div className="text-muted small">
                Origen: {describirOrigenConciliacion(fila.conciliacionOrigen)}
              </div>
            ) : null}
          </>
        )}
      </td>
      {extraColumn ? (
        <td className="align-top">{extraColumn}</td>
      ) : null}
    </tr>
  );
};

type AutoConciliacionTabsProps = {
  autoBasesCargando: boolean;
  totals: {
    sistema: number;
    monchis: number;
    coincidencias: number;
    pendientesSistema: number;
    pendientesMonchis: number;
    sugeridos: number;
  };
  autoError: string | null;
  autoConciliarMensaje: string | null;
  onCerrarMensaje: () => void;
  autoTab: "summary" | "found" | "suggested" | "missing";
  setAutoTab: (tab: "summary" | "found" | "suggested" | "missing") => void;
  autoResumen: ResumenFila[];
  autoCoincidenciasBaseFiltradas: CoincidenciaAutomatica[];
  autoCoincidenciasBaseLength: number;
  autoCoincidenciasPendientesFiltradas: CoincidenciaAutomatica[];
  autoCoincidenciasPendientesSet: Set<string>;
  autoBuscar: string;
  setAutoBuscar: (valor: string) => void;
  autoSugeridosDuplicadosLength: number;
  autoSugeridosFiltrados: CoincidenciaAutomatica[];
  autoCargando: boolean;
  autoMissingTab: "sistema" | "monchis";
  setAutoMissingTab: (tab: "sistema" | "monchis") => void;
  autoPendientesSistemaFiltrados: RegistroPendienteConciliacion[];
  autoPendientesMonchisFiltrados: RegistroPendienteConciliacion[];
  totalSinCoincidencias: number;
  onConciliarManual?: (fila: CoincidenciaAutomatica) => void;
  onRehacerManual?: (fila: CoincidenciaAutomatica) => void;
  manualProcesandoId?: string | null;
  manualConciliadas: Set<string>;
  puedeRehacerManual?: boolean;
  actualizandoEstado?: boolean;
  controlesDeshabilitados?: boolean;
  foundPagination: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
  };
  suggestedPagination: {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
  };
  missingPagination: {
    sistema: {
      page: number;
      pageSize: number;
      total: number;
      onPageChange: (page: number) => void;
    };
    monchis: {
      page: number;
      pageSize: number;
      total: number;
      onPageChange: (page: number) => void;
    };
  };
};

const MonchisAutoConciliacionTabs: React.FC<AutoConciliacionTabsProps> = ({
  autoBasesCargando,
  totals,
  autoError,
  autoConciliarMensaje,
  onCerrarMensaje,
  autoTab,
  setAutoTab,
  autoResumen,
  autoCoincidenciasBaseFiltradas,
  autoCoincidenciasBaseLength,
  autoCoincidenciasPendientesFiltradas,
  autoCoincidenciasPendientesSet,
  autoBuscar,
  setAutoBuscar,
  autoSugeridosDuplicadosLength,
  autoSugeridosFiltrados,
  autoCargando,
  autoMissingTab,
  setAutoMissingTab,
  autoPendientesSistemaFiltrados,
  autoPendientesMonchisFiltrados,
  totalSinCoincidencias,
  onConciliarManual,
  onRehacerManual,
  manualProcesandoId,
  manualConciliadas,
  puedeRehacerManual = false,
  actualizandoEstado = false,
  controlesDeshabilitados = false,
  foundPagination,
  suggestedPagination,
  missingPagination,
}) => (
  <>
    {autoBasesCargando ? (
      <div className="text-center py-4 text-muted">
        <Spinner className="me-2" /> Cargando datos de conciliación…
      </div>
    ) : (
      <>
        <Row className="g-3 mb-4">
          <Col md={4}>
            <div className="p-3 border rounded h-100">
              <div className="text-muted small">Sistema de gestión</div>
              <div className="fs-4 fw-semibold text-primary">{totals.sistema}</div>
              <div className="text-muted small">
                Coincidencias: {autoCoincidenciasBaseFiltradas.length}
              </div>
              <div className="text-muted small">
                Pendientes: {totals.pendientesSistema} · No conciliados
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="p-3 border rounded h-100">
              <div className="text-muted small">Monchis</div>
              <div className="fs-4 fw-semibold text-success">{totals.monchis}</div>
              <div className="text-muted small">
                Coincidencias: {autoCoincidenciasBaseFiltradas.length}
              </div>
              <div className="text-muted small">
                Pendientes: {totals.pendientesMonchis} · No conciliados
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="p-3 border rounded h-100">
              <div className="text-muted small">Resumen</div>
              <div className="fs-4 fw-semibold text-dark">{totals.coincidencias}</div>
              <div className="text-muted small">Sistema pendientes: {totals.pendientesSistema}</div>
              <div className="text-muted small">Monchis pendientes: {totals.pendientesMonchis}</div>
              <div className="text-muted small">Sugeridos duplicados: {totals.sugeridos}</div>
            </div>
          </Col>
        </Row>

        {autoError && <Alert color="danger">{autoError}</Alert>}
        {autoConciliarMensaje && (
          <Alert color="success" toggle={onCerrarMensaje}>
            {autoConciliarMensaje}
          </Alert>
        )}
        <Nav tabs className="mb-3">
          <NavItem>
            <NavLink active={autoTab === "summary"} style={{ cursor: "pointer" }} onClick={() => setAutoTab("summary")}>
              Vista rápida de diferencias
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink active={autoTab === "found"} style={{ cursor: "pointer" }} onClick={() => setAutoTab("found")}>
              Coincidencias encontradas
              <Badge color="secondary" pill className="ms-2">
                {autoCoincidenciasBaseFiltradas.length}
              </Badge>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              active={autoTab === "suggested"}
              style={{ cursor: "pointer" }}
              onClick={() => setAutoTab("suggested")}
            >
              Sugeridos para revisión
              <Badge color="secondary" pill className="ms-2">
                {autoSugeridosDuplicadosLength}
              </Badge>
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              active={autoTab === "missing"}
              style={{ cursor: "pointer" }}
              onClick={() => setAutoTab("missing")}
            >
              Coincidencias no encontradas
              <Badge color="secondary" pill className="ms-2">
                {totalSinCoincidencias}
              </Badge>
            </NavLink>
          </NavItem>
        </Nav>

        {autoTab === "summary" ? (
          <>
            <Row className="g-3 mb-3">
              <Col md={6}>
                <div className="p-3 border rounded h-100 bg-light">
                  <div className="text-muted small">Coincidencias encontradas</div>
                  <div className="fs-4 fw-semibold text-primary">{autoCoincidenciasBaseFiltradas.length}</div>
                  <div className="text-muted small">Total registros analizados: {autoCoincidenciasBaseLength}</div>
                </div>
              </Col>
              <Col md={6}>
                <div className="p-3 border rounded h-100 bg-light">
                  <div className="text-muted small">Coincidencias no encontradas</div>
                  <div className="fs-4 fw-semibold text-warning">{autoSugeridosDuplicadosLength}</div>
                  <div className="text-muted small">Marcados para revisión manual antes de conciliar.</div>
                </div>
              </Col>
            </Row>
            <p className="text-muted">Vista rápida de diferencias entre lo conciliado y lo pendiente por fecha.</p>
            {autoResumen.length > 0 ? (
              <div className="table-responsive">
                <Table hover className="align-middle table-nowrap">
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Coincidencias</th>
                      <th>Diferencias</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoResumen.map((fila) => {
                      const sistemaTotalTickets =
                        fila.sistema.conciliados.tickets + fila.sistema.pendientes.tickets;
                      const sistemaTotalMonto =
                        fila.sistema.conciliados.monto + fila.sistema.pendientes.monto;
                      const monchisTotalTickets =
                        fila.monchis.conciliados.tickets + fila.monchis.pendientes.tickets;
                      const monchisTotalMonto =
                        fila.monchis.conciliados.monto + fila.monchis.pendientes.monto;
                      const diffTickets = sistemaTotalTickets - monchisTotalTickets;
                      const diffMonto = sistemaTotalMonto - monchisTotalMonto;
                      const diffClase =
                        diffTickets === 0 && Math.abs(diffMonto) < 0.5
                          ? "text-success"
                          : diffMonto > 0
                            ? "text-warning"
                            : "text-danger";
                      return (
                        <tr key={fila.clave}>
                          <td>
                            <div className="fw-semibold">{fila.fechaTexto}</div>
                          </td>
                          <td className="bg-light">
                            <div className="row g-3 text-center">
                              <div className="col-md-6 border-end">
                                <div className="fw-semibold">Sistema</div>
                                <div className="text-muted small">
                                  Conciliados: {fila.sistema.conciliados.tickets} ·{" "}
                                  {formatCurrencyGs(fila.sistema.conciliados.monto)}
                                </div>
                                <div className="text-muted small">
                                  Pendientes: {fila.sistema.pendientes.tickets} ·{" "}
                                  {formatCurrencyGs(fila.sistema.pendientes.monto)}
                                </div>
                                <div className="fw-semibold">
                                  Total: {sistemaTotalTickets} · {formatCurrencyGs(sistemaTotalMonto)}
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="fw-semibold">Monchis</div>
                                <div className="text-muted small">
                                  Conciliados: {fila.monchis.conciliados.tickets} ·{" "}
                                  {formatCurrencyGs(fila.monchis.conciliados.monto)}
                                </div>
                                <div className="text-muted small">
                                  Pendientes: {fila.monchis.pendientes.tickets} ·{" "}
                                  {formatCurrencyGs(fila.monchis.pendientes.monto)}
                                </div>
                                <div className="fw-semibold">
                                  Total: {monchisTotalTickets} · {formatCurrencyGs(monchisTotalMonto)}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="row g-3 text-center">
                              <div className="col-md-6 border-end">
                                <div className="fw-semibold">Sistema</div>
                                <div className="text-muted small">
                                  Pendientes: {fila.sistema.pendientes.tickets} ·{" "}
                                  {formatCurrencyGs(fila.sistema.pendientes.monto)}
                                </div>
                              </div>
                              <div className="col-md-6">
                                <div className="fw-semibold">Monchis</div>
                                <div className="text-muted small">
                                  Pendientes: {fila.monchis.pendientes.tickets} ·{" "}
                                  {formatCurrencyGs(fila.monchis.pendientes.monto)}
                                </div>
                              </div>
                              <div className="col-12 mt-2">
                                <div className={`${diffClase} fw-semibold`}>
                                  Diferencia global: {diffTickets >= 0 ? `+${diffTickets}` : diffTickets} tickets ·{" "}
                                  {diffMonto >= 0 ? `+${formatCurrencyGs(diffMonto)}` : formatCurrencyGs(diffMonto)}
                                </div>
                              </div>
                              <div className="col-12 mt-2">
                                <div className="text-info fw-semibold">
                                  Sugeridos para revisión: {fila.sugeridos.tickets} ·{" "}
                                  {formatCurrencyGs(fila.sugeridos.monto)}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center text-muted py-3">
                No tenemos datos para calcular diferencias en este rango.
              </div>
            )}
          </>
        ) : autoTab === "found" ? (
          autoCoincidenciasBaseLength > 0 ? (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div className="text-muted small">
                  Mostrando {autoCoincidenciasBaseFiltradas.length} de {autoCoincidenciasBaseLength} coincidencias · Pendientes
                  por conciliar: {autoCoincidenciasPendientesFiltradas.length}
                </div>
                <div style={{ minWidth: 280 }}>
                  <InputGroup>
                    <InputGroupText>
                      <i className="ri-search-line" />
                    </InputGroupText>
                    <Input
                      value={autoBuscar}
                      onChange={(e) => setAutoBuscar(e.target.value)}
                      placeholder="Filtrar por sucursal, movimiento, forma de pago…"
                      disabled={controlesDeshabilitados}
                    />
                  </InputGroup>
                </div>
              </div>
              {autoCoincidenciasBaseFiltradas.length > 0 ? (
                <div className="table-responsive">
                  <Table hover className="align-middle text-start">
                    <thead className="table-light">
                      <tr>
                        <th style={{ minWidth: 280 }}>Sistema de gestión</th>
                        <th style={{ minWidth: 280 }}>Monchis</th>
                        <th style={{ minWidth: 220 }}>Estado de conciliación</th>
                      </tr>
                    </thead>
                    <tbody>
                      {autoCoincidenciasBaseFiltradas
                        .slice(
                          foundPagination.page * foundPagination.pageSize,
                          foundPagination.page * foundPagination.pageSize + foundPagination.pageSize,
                        )
                        .map((fila) => {
                          const clave = `${fila.registroId ?? ""}-${fila.movimientoId ?? ""}`;
                          const conciliacionActiva =
                            Boolean(fila.conciliacionId) && fila.conciliacionEstado === "conciliado";
                          const pendiente =
                            autoCoincidenciasPendientesSet.has(clave) || !conciliacionActiva;
                        return (
                          <CoincidenciaDetalleRow
                            key={`coincidencia-${fila.registroId}-${fila.movimientoId}`}
                            fila={fila}
                            estadoColor={pendiente ? "warning" : "success"}
                            estadoTexto={pendiente ? "Pendiente de conciliación" : "Coincidencia registrada"}
                            estadoDescripcion={
                              pendiente
                                ? "Este par aún no fue conciliado automáticamente. Podés guardarlo desde aquí."
                                : "La conciliación ya se registró anteriormente."
                            }
                            actualizandoEstado={actualizandoEstado}
                          />
                        );
                      })}
                    </tbody>
                  </Table>
                  {autoCoincidenciasBaseFiltradas.length > foundPagination.pageSize && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted">
                        Página {foundPagination.page + 1} de{" "}
                        {Math.ceil(foundPagination.total / foundPagination.pageSize)} · Total {foundPagination.total} registros
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          color="light"
                          disabled={foundPagination.page === 0}
                          onClick={() => foundPagination.onPageChange(Math.max(foundPagination.page - 1, 0))}
                        >
                          Anterior
                        </Button>
                        <Button
                          color="light"
                          disabled={foundPagination.page + 1 >= Math.ceil(foundPagination.total / foundPagination.pageSize)}
                          onClick={() =>
                            foundPagination.onPageChange(
                              Math.min(
                                foundPagination.page + 1,
                                Math.ceil(foundPagination.total / foundPagination.pageSize) - 1,
                              ),
                            )
                          }
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-muted py-3">
                  No encontramos coincidencias que coincidan con la búsqueda aplicada.
                </div>
              )}
            </>
          ) : !autoCargando ? (
            <div className="text-center text-muted py-3">Aún no encontramos coincidencias en el rango dado.</div>
          ) : null
        ) : autoTab === "suggested" ? (
          autoSugeridosDuplicadosLength > 0 ? (
            <>
              <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                <div className="text-muted small">
                  Mostrando {autoSugeridosFiltrados.length} de {autoSugeridosDuplicadosLength} sugeridos
                </div>
                <div style={{ minWidth: 280 }}>
                  <InputGroup>
                    <InputGroupText>
                      <i className="ri-search-line" />
                    </InputGroupText>
                    <Input
                      value={autoBuscar}
                      onChange={(e) => setAutoBuscar(e.target.value)}
                      placeholder="Filtrar por sucursal, movimiento, forma de pago…"
                      disabled={controlesDeshabilitados}
                    />
                  </InputGroup>
                </div>
              </div>
              {autoSugeridosFiltrados.length > 0 ? (
                <div className="table-responsive">
                  <Table hover className="align-middle text-start">
                    <thead className="table-light">
                      <tr>
                        <th style={{ minWidth: 280 }}>Sistema de gestión</th>
                        <th style={{ minWidth: 280 }}>Monchis</th>
                        <th style={{ minWidth: 220 }}>Estado de conciliación</th>
                        <th style={{ minWidth: 200 }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {autoSugeridosFiltrados
                        .slice(
                          suggestedPagination.page * suggestedPagination.pageSize,
                          suggestedPagination.page * suggestedPagination.pageSize + suggestedPagination.pageSize,
                        )
                        .map((fila) => {
                          const clave = `${fila.registroId ?? ""}-${fila.movimientoId ?? ""}`;
                          const procesando = manualProcesandoId === clave;
                          const conciliacionActiva =
                            Boolean(fila.conciliacionId) && fila.conciliacionEstado === "conciliado";
                          const conciliada = manualConciliadas.has(clave) || conciliacionActiva;
                        const estadoColor = conciliada ? "success" : "info";
                        const estadoTexto = conciliada
                          ? "Conciliación manual registrada"
                          : "Sugerido para conciliación manual";
                        const estadoDescripcion = conciliada
                          ? "Esta coincidencia ya fue registrada manualmente. Podés liberarla para volver a conciliarla si es necesario."
                          : "Detectamos movimientos duplicados para esta clave. Revisá cuál corresponde antes de tomar acción.";
                        return (
                          <CoincidenciaDetalleRow
                            key={`sugerido-${fila.registroId}-${fila.movimientoId}`}
                            fila={fila}
                            estadoColor={estadoColor}
                            estadoTexto={estadoTexto}
                            estadoDescripcion={estadoDescripcion}
                            actualizandoEstado={actualizandoEstado}
                            extraColumn={
                              conciliada ? (
                                <>
                                  <div className="text-muted small">
                                    Conciliación manual registrada. Recomendamos revisar comprobantes impresos de Monchis y
                                    registros del sistema cuando existan múltiples coincidencias por monto, sucursal, turno y fecha.
                                    El usuario es responsable del ajuste.
                                  </div>
                                  {puedeRehacerManual ? (
                                    <Button
                                      size="sm"
                                      color="warning"
                                      outline
                                      className="mt-2"
                                      disabled={!onRehacerManual || procesando || controlesDeshabilitados}
                                      onClick={() => onRehacerManual?.(fila)}
                                    >
                                      {procesando ? (
                                        <>
                                          <Spinner size="sm" className="me-2" /> Liberando…
                                        </>
                                      ) : (
                                        <>
                                          <i className="ri-arrow-go-back-line me-2" />
                                          Liberar coincidencia
                                        </>
                                      )}
                                    </Button>
                                  ) : null}
                                </>
                              ) : (
                                <>
                                  <Button
                                    size="sm"
                                    color="primary"
                                    disabled={!onConciliarManual || procesando || controlesDeshabilitados}
                                    onClick={() => onConciliarManual?.(fila)}
                                  >
                                    {procesando ? (
                                      <>
                                        <Spinner size="sm" className="me-2" /> Conciliando…
                                      </>
                                    ) : (
                                      <>
                                        <i className="ri-hand-coin-line me-2" />
                                        Conciliar manualmente
                                      </>
                                    )}
                                  </Button>
                                  <div className="text-muted small mt-2">
                                    Sugerimos comparar comprobantes impresos de Monchis con los registros del sistema de gestión
                                    cuando haya múltiples coincidencias de monto, sucursal, turno y fecha. La conciliación manual
                                    es responsabilidad del usuario.
                                  </div>
                                </>
                              )
                            }
                          />
                        );
                      })}
                    </tbody>
                  </Table>
                  {autoSugeridosFiltrados.length > suggestedPagination.pageSize && (
                    <div className="d-flex justify-content-between align-items-center mt-3">
                      <div className="text-muted">
                        Página {suggestedPagination.page + 1} de{" "}
                        {Math.ceil(suggestedPagination.total / suggestedPagination.pageSize)} · Total{" "}
                        {suggestedPagination.total} registros
                      </div>
                      <div className="d-flex gap-2">
                        <Button
                          color="light"
                          disabled={suggestedPagination.page === 0}
                          onClick={() => suggestedPagination.onPageChange(Math.max(suggestedPagination.page - 1, 0))}
                        >
                          Anterior
                        </Button>
                        <Button
                          color="light"
                          disabled={
                            suggestedPagination.page + 1 >=
                            Math.ceil(suggestedPagination.total / suggestedPagination.pageSize)
                          }
                          onClick={() =>
                            suggestedPagination.onPageChange(
                              Math.min(
                                suggestedPagination.page + 1,
                                Math.ceil(suggestedPagination.total / suggestedPagination.pageSize) - 1,
                              ),
                            )
                          }
                        >
                          Siguiente
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-muted py-3">
                  No encontramos sugeridos que coincidan con la búsqueda aplicada.
                </div>
              )}
            </>
          ) : !autoCargando ? (
            <div className="text-center text-muted py-3">
              No detectamos sugeridos para el rango seleccionado.
            </div>
          ) : null
        ) : (
          <>
            <Nav pills className="mb-3">
              <NavItem>
                <NavLink
                  active={autoMissingTab === "sistema"}
                  style={{ cursor: "pointer" }}
                  onClick={() => setAutoMissingTab("sistema")}
                >
                  Sistema de gestión
                  <Badge color="secondary" pill className="ms-2">
                    {autoPendientesSistemaFiltrados.length}
                  </Badge>
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  active={autoMissingTab === "monchis"}
                  style={{ cursor: "pointer" }}
                  onClick={() => setAutoMissingTab("monchis")}
                >
                  Monchis
                  <Badge color="secondary" pill className="ms-2">
                    {autoPendientesMonchisFiltrados.length}
                  </Badge>
                </NavLink>
              </NavItem>
            </Nav>
            <p className="text-muted">
              Revisá los registros del origen seleccionado que todavía no tienen una contraparte. Podés usarlos como referencia
              para decidir si forzás una conciliación manual.
            </p>
              {(() => {
                const sinCoincidenciasSeleccionadas =
                  autoMissingTab === "sistema" ? autoPendientesSistemaFiltrados : autoPendientesMonchisFiltrados;
                const activo =
                  autoMissingTab === "sistema" ? missingPagination.sistema : missingPagination.monchis;
                const etiquetaOrigen = autoMissingTab === "sistema" ? "el sistema de gestión" : "Monchis";
                return (
                  <div className="table-responsive">
                  <Table className="align-middle table-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th>Referencia</th>
                        <th>Fecha · hora</th>
                        <th>Sucursal</th>
                        <th>Monto</th>
                        <th>Forma de pago</th>
                        <th>Detalles</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sinCoincidenciasSeleccionadas.length > 0 ? (
                        sinCoincidenciasSeleccionadas
                          .slice(
                            activo.page * activo.pageSize,
                            activo.page * activo.pageSize + activo.pageSize,
                          )
                          .map((registro) => (
                            <tr key={`${autoMissingTab}-${registro.id}`}>
                            <td>
                              <div className="fw-semibold text-break">
                                {registro.tipo === "sistema"
                                  ? `Registro ${
                                      registro.registroNumeroMovimiento
                                        ? `#${registro.registroNumeroMovimiento}`
                                        : `#${abreviarIdentificador(registro.id)}`
                                    }`
                                  : `Movimiento ${
                                      registro.movimientoNroOrden
                                        ? `#${registro.movimientoNroOrden}`
                                        : `#${abreviarIdentificador(registro.id)}`
                                    }`}
                              </div>
                              <div className="text-muted small text-break">{registro.id}</div>
                              {registro.tipo === "monchis" && registro.movimientoNroTransaccion && (
                                <div className="text-muted small">Transacción #{registro.movimientoNroTransaccion}</div>
                              )}
                            </td>
                            <td>
                              {registro.fecha
                                ? formatFechaHoraTexto(registro.fecha) ?? registro.fecha
                                : "Sin fecha"}
                            </td>
                            <td>{registro.sucursal || "Sin sucursal"}</td>
                            <td>
                              {registro.monto !== null && registro.monto !== undefined
                                ? formatCurrencyGs(registro.monto)
                                : "Sin monto"}
                            </td>
                            <td>{registro.formaPago || "Sin dato"}</td>
                            <td>
                              {registro.tipo === "sistema" ? (
                                <>
                                  <div className="text-muted small">
                                    Turno: {registro.registroTurno || "Sin turno"}
                                    {registro.registroIdTurno ? ` · #${registro.registroIdTurno}` : ""}
                                  </div>
                                  <div className="text-muted small">
                                    {registro.registroIdControlCaja
                                      ? `Caja #${registro.registroIdControlCaja}`
                                      : "Sin control de caja"}
                                  </div>
                                  {registro.registroCajero && (
                                    <div className="text-muted small">Cajero: {registro.registroCajero}</div>
                                  )}
                                  {registro.registroNumeroMovimiento && (
                                    <div className="text-muted small">
                                      Movimiento #{registro.registroNumeroMovimiento}
                                    </div>
                                  )}
                                  <div className="text-muted small">
                                    {registro.observacion || "Sin observación"}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <div className="text-muted small">
                                    Estado: {registro.movimientoEstado || "Sin estado"}
                                  </div>
                                  {registro.movimientoTipoEntrega && (
                                    <div className="text-muted small">
                                      Entrega: {registro.movimientoTipoEntrega}
                                    </div>
                                  )}
                                  {registro.movimientoNroOrden && (
                                    <div className="text-muted small">
                                      Orden #{registro.movimientoNroOrden}
                                    </div>
                                  )}
                                  {registro.movimientoNroTransaccion && (
                                    <div className="text-muted small">
                                      Transacción #{registro.movimientoNroTransaccion}
                                    </div>
                                  )}
                                  <div className="text-muted small">
                                    {registro.observacion || "Sin observación"}
                                  </div>
                                </>
                              )}
                            </td>
                          </tr>
                        ))
                        ) : (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-4">
                              No detectamos registros sin coincidencia para {etiquetaOrigen} en el rango dado.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                    {sinCoincidenciasSeleccionadas.length > activo.pageSize && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                          Página {activo.page + 1} de {Math.ceil(activo.total / activo.pageSize)} · Total {activo.total} registros
                        </div>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            disabled={activo.page === 0}
                            onClick={() => activo.onPageChange(Math.max(activo.page - 1, 0))}
                          >
                            Anterior
                          </Button>
                          <Button
                            color="light"
                            disabled={activo.page + 1 >= Math.ceil(activo.total / activo.pageSize)}
                            onClick={() =>
                              activo.onPageChange(
                                Math.min(activo.page + 1, Math.ceil(activo.total / activo.pageSize) - 1),
                              )
                            }
                          >
                            Siguiente
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
          </>
        )}
      </>
    )}
  </>
);

export default MonchisAutoConciliacionTabs;
